import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtDate, StatusPill, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const STATUSES = [
  { key: "all", label: "Wszystkie" },
  { key: "NOWA", label: "Nowe" },
  { key: "W_TOKU", label: "W toku" },
  { key: "ZAMKNIETA", label: "Zamknięte" },
];

export default function Messages() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = useCallback(() => {
    adminApi.messages({ status: filter, q: q || undefined }).then(setData).catch(() => {});
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const openMsg = (m) => { setSelected(m); setStatus(m.status); setNote(m.admin_note || ""); };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateMessage(selected.id, { status, admin_note: note });
      toast.success("Zapisano zmiany.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await adminApi.deleteMessage(selected.id);
      toast.success("Wiadomość usunięta z bazy.");
      setConfirmDel(false); setSelected(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Wiadomości" subtitle="Zgłoszenia z formularza kontaktowego" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              filter === s.key ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"
            }`}
          >
            {s.label}{data?.statusCounts?.[s.key] != null ? ` (${data.statusCounts[s.key]})` : ""}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj po nazwie, e-mailu, treści…"
          className="ml-auto h-10 w-full max-w-xs rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none focus:border-brand-accent/60"
        />
      </div>

      <Panel testId={ADMIN.messagesTable}>
        {!data ? <Empty>Ładowanie…</Empty> : data.items.length === 0 ? <Empty>Brak wiadomości.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead><tr className="border-b border-brand-line">
                <Th>Data</Th><Th>Nadawca</Th><Th>Wiadomość</Th><Th>Status</Th>
              </tr></thead>
              <tbody>
                {data.items.map((m) => (
                  <tr key={m.id} data-testid={ADMIN.messageRow} onClick={() => openMsg(m)} className="cursor-pointer border-b border-brand-line/60 hover:bg-brand-elevated/40">
                    <Td className="whitespace-nowrap text-brand-sec">{fmtDate(m.created_at)}</Td>
                    <Td><div className="font-medium text-brand-text">{m.name}</div><div className="text-xs text-brand-muted">{m.email}</div></Td>
                    <Td className="max-w-[320px]"><span className="line-clamp-2 text-brand-sec">{m.message}</span></Td>
                    <Td><StatusPill status={m.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Wiadomość od {selected?.name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-sm text-brand-sec">{selected.email} · {fmtDate(selected.created_at)}</div>
              <div className="rounded-card border border-brand-line bg-brand-bg/50 p-3 text-sm text-brand-text whitespace-pre-wrap">{selected.message}</div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Status</label>
                <select data-testid={ADMIN.messageStatusSelect} value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 w-full rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none">
                  <option value="NOWA">Nowa</option>
                  <option value="W_TOKU">W toku</option>
                  <option value="ZAMKNIETA">Zamknięta</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Notatka wewnętrzna</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full rounded-field border border-brand-line bg-brand-surface px-3 py-2 text-sm text-brand-text outline-none focus:border-brand-accent/60" />
              </div>
              <div className="flex justify-end gap-2">
                <button data-testid="admin-message-delete" onClick={() => setConfirmDel(true)} className="inline-flex h-10 items-center gap-1.5 rounded-field border border-brand-line px-3 text-sm text-red-300 hover:border-red-400/50">
                  <Trash2 size={14} /> Usuń
                </button>
                <a href={`mailto:${selected.email}`} className="inline-flex h-10 items-center rounded-field border border-brand-line px-4 text-sm text-brand-sec hover:text-brand-text">Odpowiedz e-mailem</a>
                <button data-testid={ADMIN.messageSaveButton} onClick={save} disabled={saving} className="inline-flex h-10 items-center rounded-field bg-brand-accent px-5 text-sm font-semibold text-brand-on disabled:opacity-60">
                  {saving ? "Zapisywanie…" : "Zapisz"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDel}
        onOpenChange={setConfirmDel}
        title="Usuń wiadomość"
        description={`Trwale usunąć wiadomość od ${selected?.name} z bazy danych?`}
        confirmLabel="Usuń"
        danger
        onConfirm={doDelete}
        testId="admin-message-delete-confirm"
      />
    </div>
  );
}
